import axiosInstance from '../services/axiosInstance';

export interface AuthInitResult {
  success: boolean;
  pluginRoute?: string;
  error?: string;
}

interface PluginDetails {
  id: string;
  name: string;
  package_name: string;
  routing: string;
  status: string;
}

/**
 * Fetch plugin details from API
 */
const fetchPluginDetails = async (pluginId: string): Promise<PluginDetails | null> => {
  try {
    const { data } = await axiosInstance.get(`/studio/cms/plugins/${pluginId}`);
    return data.plugins[0]; // API returns array with single object
  } catch (error) {
    console.error('Error fetching plugin details:', error);
    return null;
  }
};

/**
 * Map plugin routing to application route
 * Maps plugin package names to their corresponding app routes
 */
const mapPluginRoute = (packageName: string): string => {
  const lowerPackageName = packageName.toLowerCase();

  // Restaurant / Menu plugins
  if (lowerPackageName.includes('restaurant')) {
    return '/restaurant';
  }

  // Add more plugin mappings here as they are implemented
  // Example:
  // if (lowerPackageName.includes('inventory')) {
  //   return '/inventory';
  // }

  // Default fallback - Plugin not found page
  console.warn(`⚠️ No route mapping found for plugin: ${packageName}`);
  return '/plugin-not-found';
};

/**
 * Initialize authentication from mobile app (Web Only)
 * This function extracts auth data passed from the React Native WebView
 * @returns AuthInitResult with success status and plugin route
 */
export const initAuthFromMobile = async (): Promise<AuthInitResult> => {
  try {
    console.log('🚀 Starting SSO initialization...');
    console.log('📍 Current URL:', window.location.href);

    // Method 1: Get from URL parameters
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const orgId = params.get('orgId');
    const orgName = params.get('orgName');
    const pluginId = params.get('pluginId');
    const userId = params.get('userId');
    const userEmail = params.get('userEmail');
    const userName = params.get('userName');

    console.log('📥 URL Params:', { token: token ? 'present' : 'missing', orgId, orgName, pluginId, userId, userEmail, userName });

    // Method 2: Get from injected JavaScript
    const mobileAuth = (window as any).MOBILE_AUTH;
    console.log('💉 Injected Auth:', mobileAuth ? 'present' : 'missing');

    // Use URL params first, fall back to injected data
    const finalToken = token || mobileAuth?.token;
    const finalOrgId = orgId || mobileAuth?.orgId;
    const finalOrgName = orgName || mobileAuth?.orgName;
    const finalPluginId = pluginId || mobileAuth?.pluginId;
    const finalUserId = userId || mobileAuth?.userId;
    const finalUserEmail = userEmail || mobileAuth?.userEmail;
    const finalUserName = userName || mobileAuth?.userName;

    // Validate required auth data
    if (!finalToken) {
      console.error('⚠️ No auth token received from mobile');
      return { success: false, error: 'Missing authentication token' };
    }

    if (!finalOrgId) {
      console.error('⚠️ No organization ID received from mobile');
      return { success: false, error: 'Missing organization ID' };
    }

    if (!finalPluginId) {
      console.error('⚠️ No plugin ID received from mobile');
      return { success: false, error: 'Missing plugin ID' };
    }

    // Store auth data in localStorage
    localStorage.setItem('web_auth_token', finalToken);
    localStorage.setItem('web_org_id', finalOrgId);
    if (finalOrgName) localStorage.setItem('web_org_name', finalOrgName);
    if (finalUserId) localStorage.setItem('web_user_id', finalUserId);
    if (finalUserEmail) localStorage.setItem('web_user_email', finalUserEmail);
    if (finalUserName) localStorage.setItem('web_user_name', finalUserName);

    console.log('✅ Auth data stored in localStorage');

    // Fetch plugin details from API
    console.log(`🔍 Fetching plugin details for: ${finalPluginId}`);
    const pluginDetails = await fetchPluginDetails(finalPluginId);

    if (!pluginDetails) {
      console.error('⚠️ Failed to fetch plugin details');
      return { success: false, error: 'Failed to load plugin information' };
    }

    console.log('✅ Plugin details:', pluginDetails);

    // Store plugin information in localStorage
    localStorage.setItem('web_plugin_id', pluginDetails.id);
    localStorage.setItem('web_plugin_name', pluginDetails.name);
    localStorage.setItem('web_plugin_package', pluginDetails.package_name);

    // Determine route based on plugin routing
    const pluginRoute = mapPluginRoute(pluginDetails.package_name);
    console.log(`✅ Routing to: ${pluginRoute}`);

    return {
      success: true,
      pluginRoute
    };
  } catch (error) {
    console.error('Error initializing auth:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Send message to React Native app (for future use)
 */
export const sendMessageToMobile = (message: any) => {
  if ((window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
  } else {
    console.warn('ReactNativeWebView not available');
  }
};
